import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import classNames from 'classnames';

import { formatPercentage, Sizes } from '@ratehub/base-ui';

import MESSAGES from '../definitions/Messages';
import CreditCardSynopsisShape from '../definitions/CreditCardSynopsisShape';
import SPENDING_CATEGORY_MESSAGES from '../definitions/SpendingCategoryMessages';
import isEarningsAPercentage from '../functions/isEarningsAPercentage';
import getSpendingCategoryDescription from '../functions/getSpendingCategoryDescription';


function CardEarningTable({
    subTitle,
    subTitleSize = 'medium',
    cardInfo,
    productType,
    className,
    ...otherProps
}) {
    const intl = useIntl();
    const isEarningsAsPercentage = isEarningsAPercentage({
        productType,
        cardId: cardInfo.id,
        cardProductType: cardInfo.productType,
    });

    const rows = cardInfo.earningsDescription.map(entry => {
        const categories = getSpendingCategoryDescription(entry.types, intl);

        return {
            amount: isEarningsAsPercentage
                ? formatPercentage(entry.rate, intl, 0, 2)
                : intl.formatMessage(MESSAGES.pointsPerDollar, { multiplier: entry.rate, oneDollar: 1 }),
            description: intl.formatMessage(SPENDING_CATEGORY_MESSAGES.DESCRIPTION, { categories }),
        };
    });

    return (
        <If condition={rows.length}>
            <div
                className={classNames(className, 'rh-mb-1_5')}
                {...otherProps}
            >
                <If condition={subTitle}>
                    <h4 className={classNames('rh-mt-0 rh-mb-1', {
                        'rh-title-s': subTitleSize === 'large',
                        'rh-title-2xs': subTitleSize === 'medium' })}
                    >
                        {subTitle}
                    </h4>
                </If>

                <div className="earningGrid rh-display-grid rh-bt-1px-solid-stone">
                    <For
                        each="item"
                        of={rows}
                        index="index"
                    >
                        <strong
                            className="rh-title-xs rh-m-0 rh-py-1 rh-pr-1_5 rh-bb-1px-solid-stone leading-m"
                            key={index}
                            data-test-name="reward-amount"
                        >
                            {item.amount}
                        </strong>
                        <p
                            className="description rh-text-s rh-m-0 rh-py-1 rh-bb-1px-solid-stone leading-m"
                            key={`${index}-description`}
                        >
                            {item.description}
                        </p>
                    </For>
                </div>
            </div>
        </If>
    );
}

CardEarningTable.propTypes = {
    subTitle: PropTypes.node,
    subTitleSize: PropTypes.oneOf([ 'medium', 'large' ]),

    cardInfo: PropTypes.shape({
        id: PropTypes.string.isRequired,
        productType: CreditCardSynopsisShape.productType,
        earningsDescription: CreditCardSynopsisShape.earningsDescription.isRequired,
    }).isRequired,

    productType: PropTypes.string,
    className: PropTypes.string,
};


/*
    - equalize baselines between two differently sized elements
    - requires knowledge of font sizes used by rh-title-xs and rh-text-s
*/
const equalizingBaselineAdjustment = parseFloat(Sizes.FONTS.M) - parseFloat(Sizes.FONTS.XS);

export default styled(observer(CardEarningTable))`
    > .earningGrid {
        grid: 1fr / max-content 1fr;

        > .description {
            margin-top: ${equalizingBaselineAdjustment}rem;
        }
    }
`;
